import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY REVIEW BOARD
   ----------------------------------------------------------------------------
   LIVRABLE DE SORTIE DU MODULE ACTIVITY
   - rapport imprimable A4 / exportable PDF
   - pièce documentaire de synthèse
   - destiné au PM / PMO / MOA / Direction de projet
   - suit la grammaire du template de page Corelia
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.panel || "#cfd8dd",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",

  shadowXs: theme?.shadows?.xs || "0 1px 3px rgba(11,39,53,0.08)",
  shadowSm: theme?.shadows?.sm || "0 3px 10px rgba(11,39,53,0.08)",

  radiusLg: theme?.borders?.radius?.lg || "6px",
  radiusXl: theme?.borders?.radius?.xl || "8px",
};

/* ============================================================================
   STYLES PARTAGÉS LOCAUX
============================================================================ */

const S = {
  page: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    //background: T.bg,
  },

  pageHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    paddingBottom: 18,
  },

  pageHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  overline: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.verySoft,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },

  pageTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.03rem",
    lineHeight: 1.14,
    color: T.text,
    fontWeight: 600,
  },

  toolbarGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 12,
  },

  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    flexWrap: "wrap",
    justifyContent: "flex-start",
    minWidth: 0,
  },

  toolbarCenter: {
    display: "flex",
    justifyContent: "center",
    minWidth: 0,
  },

  toolbarRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    flexWrap: "wrap",
    minWidth: 0,
  },

  pillsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    flexWrap: "nowrap",
  },

  metricWrap: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },

  metricLabel: {
    fontSize: "0.72rem",
    color: T.verySoft,
    lineHeight: 1,
  },

  metricValue: {
    fontSize: "0.82rem",
    lineHeight: 1,
    fontWeight: 600,
  },

  reportWrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    background: "transparent",
  },

  reportSheet: {
    width: "210mm",
    minHeight: "297mm",
    margin: "0 auto",
    background: "#ffffff",
    boxSizing: "border-box",
    padding: "16mm 14mm 16mm 14mm",
    boxShadow: T.shadowXs,
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusLg,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  reportBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  reportTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.32rem",
    lineHeight: 1.08,
    fontWeight: 700,
    color: T.text,
  },

  reportSubtitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.82rem",
    lineHeight: 1.45,
    color: T.soft,
  },

  sectionTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.92rem",
    lineHeight: 1.15,
    fontWeight: 600,
    color: T.corelia,
  },

  sectionText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.79rem",
    lineHeight: 1.52,
    color: T.text,
  },

  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },

  metaCard: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },

  metaLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.66rem",
    lineHeight: 1.1,
    color: T.verySoft,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  metaValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.80rem",
    lineHeight: 1.3,
    color: T.text,
    fontWeight: 600,
    wordBreak: "break-word",
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  kpiCard: {
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusLg,
    padding: 10,
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    boxSizing: "border-box",
  },

  kpiLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.66rem",
    lineHeight: 1.1,
    color: T.verySoft,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  kpiValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.08rem",
    lineHeight: 1,
    color: T.text,
    fontWeight: 700,
  },

  kpiNote: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.70rem",
    lineHeight: 1.35,
    color: T.soft,
  },

  tableWrap: {
    width: "100%",
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusLg,
    overflow: "hidden",
    background: "#ffffff",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.70rem",
    lineHeight: 1.2,
    color: T.text,
    fontWeight: 600,
    textAlign: "left",
    background: "#f8fafc",
    borderBottom: `1px solid ${T.border}`,
    padding: "8px 9px",
  },

  td: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.35,
    color: T.text,
    padding: "8px 9px",
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: "top",
  },

  stripGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  stripCard: {
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusLg,
    padding: 10,
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  stripLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.68rem",
    lineHeight: 1.1,
    color: T.verySoft,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  stripTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.82rem",
    lineHeight: 1.2,
    color: T.text,
    fontWeight: 600,
  },

  stripNote: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.35,
    color: T.soft,
  },

  node: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: 999,
    border: `1.5px solid ${T.corelia}`,
    color: T.corelia,
    fontSize: "0.74rem",
    fontWeight: 700,
    marginRight: 6,
    marginBottom: 6,
    background: "#ffffff",
    boxSizing: "border-box",
  },

  legendLine: {
    width: 18,
    height: 0,
    borderTop: `2px solid ${T.corelia}`,
  },

  bulletList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  bulletRow: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.45,
    color: T.text,
  },
};

/* ============================================================================
   COMPOSANTS STANDARDS DE PAGE
============================================================================ */

const TopToolbar = ({ leftContent, centerContent, rightContent }) => (
  <div style={S.toolbarGrid}>
    <div style={S.toolbarLeft}>{leftContent}</div>
    <div style={S.toolbarCenter}>{centerContent}</div>
    <div style={S.toolbarRight}>{rightContent}</div>
  </div>
);

const MetricInline = ({ label, value, valueColor = T.text }) => (
  <div style={S.metricWrap}>
    <span style={S.metricLabel}>{label}</span>
    <span style={{ ...S.metricValue, color: valueColor }}>{value}</span>
  </div>
);

const PillSwitch = ({ value, onChange, options = [] }) => (
  <div style={S.pillsRow}>
    {options.map((option) => {
      const active = option === value;

      return (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          style={{
            height: 28,
            padding: "0 10px",
            borderRadius: 999,
            border: `1px solid ${active ? "rgba(14,63,86,0.18)" : T.border}`,
            background: active ? "rgba(14,63,86,0.05)" : "#ffffff",
            color: active ? T.corelia : T.soft,
            fontSize: "0.71rem",
            fontWeight: 500,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {option}
        </button>
      );
    })}
  </div>
);

const LegendDot = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
      }}
    />
    <span style={{ fontSize: "0.72rem", color: T.verySoft }}>{label}</span>
  </div>
);

const PageHeader = ({
  overline = "Page overline",
  title = "Titre de page",
  metrics = [],
  filterOptions = [],
  activeFilter,
  onFilterChange,
  legends = [],
}) => (
  <div style={S.pageHeader}>
    <div style={S.pageHeaderText}>
      <div style={S.overline}>{overline}</div>
      <div style={S.pageTitle}>{title}</div>
    </div>

    <TopToolbar
      leftContent={
        <>
          {metrics.map((item) => (
            <MetricInline
              key={item.label}
              label={item.label}
              value={item.value}
              valueColor={item.valueColor || T.text}
            />
          ))}
        </>
      }
      centerContent={
        <PillSwitch
          value={activeFilter}
          onChange={onFilterChange}
          options={filterOptions}
        />
      }
      rightContent={
        <>
          {legends.map((item) => (
            <LegendDot
              key={item.label}
              color={item.color}
              label={item.label}
            />
          ))}
        </>
      }
    />
  </div>
);

/* ============================================================================
   MOCK DATA
============================================================================ */

const DEFAULT_REPORT = {
  project: {
    projectName: "Projet d’alimentation en eau potable – Corridor Nord",
    reference: "AEP-CMR-2026-017",
    client: "Ministère de l’Eau et de l’Énergie",
    location: "Corridor Nord – Cameroun",
    country: "Cameroun",
    type: "Infrastructure AEP / EPC",
    contract: "EPC",
    issue: "Version provisoire",
  },

  packages: [
    {
      code: "A",
      name: "Préparation plateforme et installations générales",
      stage: "Préparation",
      discipline: "Réseaux",
      zone: "Base vie / emprises",
      logic: "Par zone",
      status: "Confirmé",
      criticality: "Modérée",
      itemsCount: 5,
    },
    {
      code: "B",
      name: "Réseaux primaires DN1000",
      stage: "Infrastructure",
      discipline: "Réseaux",
      zone: "Tronçon primaire",
      logic: "Par lot métier",
      status: "Confirmé",
      criticality: "Forte",
      itemsCount: 7,
    },
    {
      code: "C",
      name: "Ouvrage principal – génie civil",
      stage: "Infrastructure",
      discipline: "Génie civil",
      zone: "Station de traitement",
      logic: "Par zone",
      status: "Confirmé",
      criticality: "Forte",
      itemsCount: 8,
    },
    {
      code: "D",
      name: "Bloc traitement – ligne 1",
      stage: "Équipements",
      discipline: "Process",
      zone: "Station de traitement",
      logic: "Par système",
      status: "Confirmé",
      criticality: "Critique",
      itemsCount: 6,
    },
    {
      code: "E",
      name: "Poste électrique principal et contrôle",
      stage: "Équipements",
      discipline: "Électricité",
      zone: "Bloc utilités",
      logic: "Par système",
      status: "À surveiller",
      criticality: "Critique",
      itemsCount: 5,
    },
    {
      code: "F",
      name: "Essais, basculement et mise en route",
      stage: "Finalisation",
      discipline: "Process",
      zone: "Station + réseau",
      logic: "Par séquence",
      status: "À consolider",
      criticality: "Critique",
      itemsCount: 4,
    },
  ],

  planning: [
    {
      sequence: "01",
      package: "A — Préparation plateforme et installations générales",
      logic: "Démarrage / prérequis chantier",
      dependency: "Aucune",
      phase: "Préparation",
    },
    {
      sequence: "02",
      package: "B — Réseaux primaires DN1000",
      logic: "Branche parallèle 1",
      dependency: "A",
      phase: "Infrastructure",
    },
    {
      sequence: "03",
      package: "C — Ouvrage principal – génie civil",
      logic: "Branche parallèle 2",
      dependency: "A",
      phase: "Infrastructure",
    },
    {
      sequence: "04",
      package: "D — Bloc traitement – ligne 1",
      logic: "Suite GC / process",
      dependency: "C",
      phase: "Équipements",
    },
    {
      sequence: "05",
      package: "E — Poste électrique principal et contrôle",
      logic: "Convergence réseaux + GC",
      dependency: "B + C",
      phase: "Équipements",
    },
    {
      sequence: "06",
      package: "F — Essais, basculement et mise en route",
      logic: "Clôture de chaîne",
      dependency: "D + E",
      phase: "Finalisation",
    },
  ],

  interfaces: [
    {
      interface: "Génie civil ↔ Process",
      impact:
        "Supports, réservations, implantation des équipements et continuité de la ligne de traitement.",
      status: "Sensible",
      action:
        "Consolider les prérequis techniques avant engagement définitif des packages process.",
    },
    {
      interface: "Process ↔ Électricité",
      impact:
        "Câblage, contrôle-commande, séquences d’essais et mise sous tension.",
      status: "Critique",
      action:
        "Valider l’ordre logique des dépendances avant préparation du planning contractuel externe.",
    },
    {
      interface: "Réseaux ↔ Utilités",
      impact:
        "Continuité de service, accès chantier et coordination des emprises.",
      status: "Modérée",
      action: "Maintenir la coordination de zone et les jalons de transfert.",
    },
  ],

  openPoints: [
    "Valider les prérequis de mise sous tension du package E avant basculement vers F.",
    "Stabiliser la convergence D / E / F afin de limiter les reprises et les glissements de séquence.",
    "Confirmer la maille définitive du package F pour la préparation du planning contractuel externe.",
  ],
};

/* ============================================================================
   REPORT HELPERS
============================================================================ */

const getStatusColor = (status) => {
  if (status === "Confirmé") return T.greenText;
  if (status === "À surveiller") return T.orangeText;
  if (status === "À consolider") return T.caneva;
  return T.text;
};

const getCriticalityColor = (criticality) => {
  if (criticality === "Critique") return T.caneva;
  if (criticality === "Forte") return T.orangeText;
  if (criticality === "Modérée") return T.greenText;
  return T.text;
};

const formatToday = () => {
  const date = new Date();
  return date.toLocaleDateString();
};

const computeSummary = (data) => {
  const totalPackages = data.packages.length;
  const criticalPackages = data.packages.filter(
    (item) => item.criticality === "Critique"
  ).length;
  const confirmedPackages = data.packages.filter(
    (item) => item.status === "Confirmé"
  ).length;
  const totalItems = data.packages.reduce(
    (sum, item) => sum + Number(item.itemsCount || 0),
    0
  );
  const interfaceCount = data.interfaces.length;
  const readinessPct = Math.round((confirmedPackages / totalPackages) * 100);

  return {
    totalPackages,
    criticalPackages,
    confirmedPackages,
    totalItems,
    interfaceCount,
    readinessPct,
  };
};

/* ============================================================================
   REPORT ATOMS
============================================================================ */

const ReportMetaItem = ({ label, value }) => (
  <div style={S.metaCard}>
    <div style={S.metaLabel}>{label}</div>
    <div style={S.metaValue}>{value || "—"}</div>
  </div>
);

const ReportKpiCard = ({ label, value, note, tone = T.text }) => (
  <div style={S.kpiCard}>
    <div style={S.kpiLabel}>{label}</div>
    <div style={{ ...S.kpiValue, color: tone }}>{value}</div>
    <div style={S.kpiNote}>{note}</div>
  </div>
);

const SectionBlock = ({ title, children }) => (
  <div style={S.reportBlock}>
    <div style={S.sectionTitle}>{title}</div>
    {children}
  </div>
);

const TableCell = ({ children, style = {} }) => (
  <td style={{ ...S.td, ...style }}>{children}</td>
);

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const ActivityReviewBoard = ({
  reportData = DEFAULT_REPORT,
}) => {
  const [activeFilter, setActiveFilter] = useState("Rapport complet");

  const summary = useMemo(() => computeSummary(reportData), [reportData]);

  return (
    <>
      <style>{`
        @media print {
          .activity-report-sheet {
            width: 210mm !important;
            min-height: 297mm !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            page-break-after: always;
          }

          .activity-report-sheet:last-child {
            page-break-after: auto;
          }
        }
      `}</style>

      <div style={S.page}>
        <PageHeader
          overline="Activity report"
          title="Livrable de synthèse du module Activity"
          metrics={[
            { label: "Packages", value: summary.totalPackages },
            {
              label: "Critiques",
              value: summary.criticalPackages,
              valueColor: T.caneva,
            },
            {
              label: "Readiness",
              value: `${summary.readinessPct}%`,
              valueColor: T.corelia,
            },
          ]}
          filterOptions={["Rapport complet", "Synthèse", "Annexes"]}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          legends={[
            { label: "Confirmé", color: T.green },
            { label: "À surveiller", color: T.orange },
            { label: "Critique", color: T.caneva },
          ]}
        />

        <div style={S.reportWrap}>
          {/* PAGE 1 */}
          <div className="activity-report-sheet" style={S.reportSheet}>
            <div style={S.reportBlock}>
              <div style={S.reportTitle}>Activity Report</div>
              <div style={S.reportSubtitle}>
                Rapport de structuration et de séquencement opérationnel du projet.
                Ce document constitue le livrable de sortie du module Activity et a
                vocation à être utilisé comme pièce de cadrage, de consultation et
                de pilotage.
              </div>
            </div>

            <SectionBlock title="Identité du projet">
              <div style={S.metaGrid}>
                <ReportMetaItem label="Projet" value={reportData.project.projectName} />
                <ReportMetaItem label="Référence" value={reportData.project.reference} />
                <ReportMetaItem label="Client" value={reportData.project.client} />
                <ReportMetaItem label="Localisation" value={reportData.project.location} />
                <ReportMetaItem label="Pays" value={reportData.project.country} />
                <ReportMetaItem label="Type" value={reportData.project.type} />
                <ReportMetaItem label="Montage" value={reportData.project.contract} />
                <ReportMetaItem label="Indice" value={reportData.project.issue} />
                <ReportMetaItem label="Date d’édition" value={formatToday()} />
              </div>
            </SectionBlock>

            <SectionBlock title="Synthèse exécutive">
              <div style={S.kpiGrid}>
                <ReportKpiCard
                  label="Packages structurés"
                  value={summary.totalPackages}
                  note="Unités pilotables définies"
                />
                <ReportKpiCard
                  label="Éléments analysés"
                  value={summary.totalItems}
                  note="Objets consolidés dans Activity"
                />
                <ReportKpiCard
                  label="Packages critiques"
                  value={summary.criticalPackages}
                  note="Points de tension majeurs"
                  tone={T.caneva}
                />
                <ReportKpiCard
                  label="Interfaces"
                  value={summary.interfaceCount}
                  note="Interfaces structurantes identifiées"
                  tone={T.orangeText}
                />
              </div>

              <div style={S.sectionText}>
                Le module Activity a permis de transformer le projet physique en
                une structure opérationnelle lisible, organisée en packages et en
                séquences. Le modèle obtenu fait apparaître une divergence initiale
                maîtrisée, une phase d’infrastructure conduite en parallèle, puis
                une convergence critique en phase équipements, avant clôture par la
                mise en route. Cette lecture fournit une base directement exploitable
                pour les arbitrages de pilotage, la préparation du planning
                contractuel externe et l’alimentation des modules Specification et
                Budget.
              </div>
            </SectionBlock>

            <SectionBlock title="Lecture de la chaîne Activity">
              <div style={S.sectionText}>
                <strong>Flow</strong> a permis de comprendre le déroulement général
                du projet. <strong>Analysis</strong> a traduit ce projet en réalité
                de production par matériaux, main d’œuvre et équipements.
                <strong> Structuring</strong> a regroupé cette matière en packages
                pilotables. <strong>Sequencing</strong> a enfin ordonné ces packages
                selon une logique de dépendance, de parallélisation et de convergence.
              </div>
            </SectionBlock>
          </div>

          {/* PAGE 2 */}
          <div className="activity-report-sheet" style={S.reportSheet}>
            <SectionBlock title="Séquence logique du projet">
              <div style={S.stripGrid}>
                <div style={S.stripCard}>
                  <div style={S.stripLabel}>Séquence</div>
                  <div style={S.stripTitle}>Préparation</div>
                  <div>
                    <span style={S.node}>A</span>
                  </div>
                  <div style={S.stripNote}>Démarrage et prérequis chantier</div>
                </div>

                <div style={S.stripCard}>
                  <div style={S.stripLabel}>Séquence</div>
                  <div style={S.stripTitle}>Infrastructure</div>
                  <div>
                    <span style={S.node}>B</span>
                    <span style={S.node}>C</span>
                  </div>
                  <div style={S.stripNote}>Deux branches parallèles structurantes</div>
                </div>

                <div style={S.stripCard}>
                  <div style={S.stripLabel}>Séquence</div>
                  <div style={S.stripTitle}>Équipements</div>
                  <div>
                    <span style={S.node}>D</span>
                    <span style={S.node}>E</span>
                  </div>
                  <div style={S.stripNote}>Convergence critique process / électricité</div>
                </div>

                <div style={S.stripCard}>
                  <div style={S.stripLabel}>Séquence</div>
                  <div style={S.stripTitle}>Finalisation</div>
                  <div>
                    <span style={S.node}>F</span>
                  </div>
                  <div style={S.stripNote}>Essais, basculement et mise en route</div>
                </div>
              </div>
            </SectionBlock>

            <SectionBlock title="Graphe synthétique des dépendances">
              <div style={{ ...S.tableWrap, padding: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={S.legendLine} />
                    <span style={S.kpiNote}>Flux principal</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        ...S.legendLine,
                        borderTop: `2px dashed ${T.orangeText}`,
                      }}
                    />
                    <span style={S.kpiNote}>Interface</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        ...S.legendLine,
                        borderTop: `3px solid ${T.caneva}`,
                      }}
                    />
                    <span style={S.kpiNote}>Liaison critique</span>
                  </div>
                </div>

                <svg
                  width="100%"
                  height="320"
                  viewBox="0 0 760 320"
                  style={{ display: "block" }}
                >
                  <text x="90" y="24" textAnchor="middle" fontSize="11" fill={T.verySoft}>
                    Préparation
                  </text>
                  <text x="260" y="24" textAnchor="middle" fontSize="11" fill={T.verySoft}>
                    Infrastructure
                  </text>
                  <text x="430" y="24" textAnchor="middle" fontSize="11" fill={T.verySoft}>
                    Équipements
                  </text>
                  <text x="610" y="24" textAnchor="middle" fontSize="11" fill={T.verySoft}>
                    Finalisation
                  </text>

                  <line x1="112" y1="120" x2="240" y2="82" stroke={T.corelia} strokeWidth="2" />
                  <line x1="112" y1="120" x2="240" y2="178" stroke={T.corelia} strokeWidth="2" />
                  <line x1="278" y1="82" x2="410" y2="92" stroke={T.orangeText} strokeWidth="2" strokeDasharray="6 4" />
                  <line x1="278" y1="178" x2="410" y2="178" stroke={T.corelia} strokeWidth="2" />
                  <line x1="278" y1="178" x2="410" y2="92" stroke={T.caneva} strokeWidth="3" />
                  <line x1="448" y1="178" x2="590" y2="120" stroke={T.caneva} strokeWidth="3" />
                  <line x1="448" y1="92" x2="590" y2="120" stroke={T.caneva} strokeWidth="3" />

                  {[
                    { x: 90, y: 120, code: "A" },
                    { x: 260, y: 82, code: "B" },
                    { x: 260, y: 178, code: "C" },
                    { x: 430, y: 178, code: "D" },
                    { x: 430, y: 92, code: "E" },
                    { x: 610, y: 120, code: "F" },
                  ].map((node) => (
                    <g key={node.code}>
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r="18"
                        fill="#ffffff"
                        stroke={T.corelia}
                        strokeWidth="2"
                      />
                      <text
                        x={node.x}
                        y={node.y + 4}
                        textAnchor="middle"
                        fontSize="12"
                        fontWeight="700"
                        fill={T.corelia}
                      >
                        {node.code}
                      </text>
                    </g>
                  ))}

                  <text x="90" y="150" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Préparation
                  </text>
                  <text x="260" y="112" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Réseaux
                  </text>
                  <text x="260" y="208" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Génie civil
                  </text>
                  <text x="430" y="208" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Process
                  </text>
                  <text x="430" y="122" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Électricité
                  </text>
                  <text x="610" y="150" textAnchor="middle" fontSize="10" fill={T.soft}>
                    Mise en route
                  </text>
                </svg>
              </div>
            </SectionBlock>
          </div>

          {/* PAGE 3 */}
          <div className="activity-report-sheet" style={S.reportSheet}>
            <SectionBlock title="Référentiel des packages">
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "8%" }}>Code</th>
                      <th style={{ ...S.th, width: "27%" }}>Package</th>
                      <th style={{ ...S.th, width: "12%" }}>Étape</th>
                      <th style={{ ...S.th, width: "13%" }}>Discipline</th>
                      <th style={{ ...S.th, width: "16%" }}>Zone</th>
                      <th style={{ ...S.th, width: "10%" }}>Éléments</th>
                      <th style={{ ...S.th, width: "7%" }}>Statut</th>
                      <th style={{ ...S.th, width: "7%" }}>Criticité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.packages.map((row, index) => (
                      <tr key={`${row.code}-${index}`}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.stage}</TableCell>
                        <TableCell>{row.discipline}</TableCell>
                        <TableCell>{row.zone}</TableCell>
                        <TableCell>{row.itemsCount}</TableCell>
                        <TableCell style={{ color: getStatusColor(row.status), fontWeight: 600 }}>
                          {row.status}
                        </TableCell>
                        <TableCell style={{ color: getCriticalityColor(row.criticality), fontWeight: 600 }}>
                          {row.criticality}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionBlock>

            <SectionBlock title="Planning logique sans dates">
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "9%" }}>Seq.</th>
                      <th style={{ ...S.th, width: "35%" }}>Package</th>
                      <th style={{ ...S.th, width: "22%" }}>Logique</th>
                      <th style={{ ...S.th, width: "16%" }}>Dépendance</th>
                      <th style={{ ...S.th, width: "18%" }}>Phase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.planning.map((row, index) => (
                      <tr key={`${row.sequence}-${index}`}>
                        <TableCell>{row.sequence}</TableCell>
                        <TableCell>{row.package}</TableCell>
                        <TableCell>{row.logic}</TableCell>
                        <TableCell>{row.dependency}</TableCell>
                        <TableCell>{row.phase}</TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionBlock>
          </div>

          {/* PAGE 4 */}
          <div className="activity-report-sheet" style={S.reportSheet}>
            <SectionBlock title="Interfaces structurantes">
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "22%" }}>Interface</th>
                      <th style={{ ...S.th, width: "34%" }}>Impact</th>
                      <th style={{ ...S.th, width: "12%" }}>Statut</th>
                      <th style={{ ...S.th, width: "32%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.interfaces.map((row, index) => (
                      <tr key={`${row.interface}-${index}`}>
                        <TableCell>{row.interface}</TableCell>
                        <TableCell>{row.impact}</TableCell>
                        <TableCell
                          style={{
                            color:
                              row.status === "Critique"
                                ? T.caneva
                                : row.status === "Sensible"
                                ? T.orangeText
                                : T.greenText,
                            fontWeight: 600,
                          }}
                        >
                          {row.status}
                        </TableCell>
                        <TableCell>{row.action}</TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionBlock>

            <SectionBlock title="Points ouverts et réserves">
              <div style={S.bulletList}>
                {reportData.openPoints.map((item, index) => (
                  <div key={`${index}-${item}`} style={S.bulletRow}>
                    {`${String(index + 1).padStart(2, "0")} · ${item}`}
                  </div>
                ))}
              </div>
            </SectionBlock>

            <SectionBlock title="Conclusion opérationnelle">
              <div style={S.sectionText}>
                Le présent rapport formalise une lecture structurée du projet au
                terme du module Activity. La chaîne de production a été organisée
                en packages pilotables, puis séquencée selon une logique compatible
                avec le pilotage réel du projet. Ce document ne constitue pas un
                planning contractuel, mais une base de référence permettant de
                préparer ce planning, de clarifier les dépendances majeures et
                d’aligner les acteurs du projet sur une compréhension commune de
                l’exécution.
              </div>
              <div style={S.sectionText}>
                En conséquence, ce rapport peut être utilisé comme pièce
                documentaire de cadrage dans un dossier de consultation, comme
                support d’arbitrage PM / PMO, et comme passerelle vers les modules
                Specification et Budget de Corelia.
              </div>
            </SectionBlock>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityReviewBoard;